import { config } from '@app/config';
import { getQueryParam } from '@mi-front/core';
import debugFn from 'debug';
import type { PostHogConfig, PostHogInterface } from 'posthog-js';
import { PostHogContext, PostHogProvider } from 'posthog-js/react';
import { useEffect, useMemo } from 'react';

const debug = debugFn('posthog');

const options: Partial<PostHogConfig> = {
  api_host: config.posthogHost,
  autocapture: false,
  // We handle pageview manually in our screen tracking hook for precision
  capture_pageview: false,
  // Useful for tracking session duration
  capture_pageleave: false,
  defaults: '2025-05-24',
  loaded: (posthog: PostHogInterface) => {
    posthog.register({ app_version: config.appVersion });
    posthog.setPersonProperties({ app_version: config.appVersion });
  },
};

export function PostHogProviderWrapper({ children }: { children: React.ReactNode }) {
  const shouldEnable = useMemo(
    () => (config.isNodeProd && config.posthogKey) || getQueryParam('enable_posthog'),
    [],
  );

  useEffect(() => {
    if (!shouldEnable) {
      debug('PostHog is disabled');
      return;
    }

    debug('PostHog is enabled');
  }, [shouldEnable]);

  if (!shouldEnable) {
    // use empty context to posthog using a global instance in usePostHog() even
    // when it is disabled
    return (
      <PostHogContext.Provider
        value={
          { client: undefined, bootstrap: undefined } as unknown as React.ComponentProps<
            typeof PostHogContext.Provider
          >['value']
        }
      >
        {children}
      </PostHogContext.Provider>
    );
  }

  return (
    <PostHogProvider apiKey={config.posthogKey!} options={options}>
      {children}
    </PostHogProvider>
  );
}
